// ============================================================================
// devopsErrors.ts — человеческие состояния панели разработки (SSoT/SRP).
//
// DevopsService отвечает FailedPrecondition (400), если в ТЕКУЩЕЙ базе нет
// поставки hydra.devops: это штатное состояние, а не сбой сервера. Отдельно
// транспортные ошибки (502/503/504 от nginx, обрыв) означают перезапуск
// сервера. Панель не должна показывать сырые коды — только понятный экран.
// ============================================================================

import { isPermissionDeniedError, isTransportError } from '@/lib/errors';

export type DevopsIssueKind = 'not_installed' | 'restarting' | 'forbidden' | 'unavailable';

export interface DevopsIssue {
  kind: DevopsIssueKind;
  title: string;
  detail: string;
}

function messageOf(e: unknown): string {
  if (e instanceof Error) return e.message.toLowerCase();
  return String(e ?? '').toLowerCase();
}

/** Состояние панели по ошибке RPC/транспорта (без сырых кодов в тексте). */
export function classifyDevopsError(e: unknown): DevopsIssue {
  const message = messageOf(e);

  if (isPermissionDeniedError(e)) {
    return {
      kind: 'forbidden',
      title: 'Нет доступа к панели разработки',
      detail: 'Нужна роль «Разработка — просмотр» или «Разработка — управление». Обратитесь к администратору базы.',
    };
  }

  const notInstalled = message.includes('не установлена')
    || (message.includes('поставк') && message.includes('разработк'))
    || message.includes('hydra.devops');
  if (notInstalled) {
    return {
      kind: 'not_installed',
      title: 'Панель разработки не установлена в этой базе',
      detail: 'В этой базе нет поставки «Разработка» (hydra.devops). Откройте базу, где она установлена, или примените поставку в конфигураторе. Другие базы не затрагиваются.',
    };
  }

  if (message.includes('не инициализирована')) {
    return {
      kind: 'unavailable',
      title: 'Панель разработки недоступна на этом сервере',
      detail: 'Сервер запущен без агента панели. Перезапустите сервер; если состояние повторяется — обратитесь к администратору.',
    };
  }

  if (isTransportError(e)) {
    return {
      kind: 'restarting',
      title: 'Сервер перезапускается',
      detail: 'Связь с сервером временно потеряна (прокси не нашёл сервер). Подождите несколько секунд и повторите.',
    };
  }

  return {
    kind: 'unavailable',
    title: 'Панель разработки недоступна',
    detail: 'Не удалось получить состояние панели. Технические подробности записаны в журнал регистрации.',
  };
}
